"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { urlFor } from "@/sanity/lib/image";

interface BlogPost {
  _id: string;
  slug: {
    current: string;
  };
  title: string;
  publishDate: string;
  categories?: Array<{
    _id: string;
    title: string;
  }>;
  featuredImage?: {
    asset: {
      _ref: string;
    };
    alt?: string;
  };
  excerpt?: string;
  author?: {
    name: string;
    image?: {
      asset: {
        _ref: string;
      };
    };
  };
}

interface BlogPostsProps {
  heading?: string;
  description?: string;
  showFilters?: boolean;
  showSearch?: boolean;
  postsPerPage?: number;
  categories?: Array<{
    _id: string;
    title: string;
  }>;
  excludeCategories?: Array<{
    _id: string;
    title: string;
  }>;
  sortBy?: string;
  layout?: "grid" | "grid-2" | "list" | "masonry";
  showExcerpt?: boolean;
  showAuthor?: boolean;
  showDate?: boolean;
  showCategory?: boolean;
  showFeaturedImage?: boolean;
  styling?: {
    backgroundColor?: string;
    customBackgroundColor?: string;
    padding?: string;
    cardStyle?: string;
  };
  posts?: BlogPost[];
}

// Placeholder posts for when no posts are found
const placeholderPosts: BlogPost[] = [
  {
    _id: "placeholder-1",
    slug: { current: "the-art-of-technical-project-management" },
    title: "The Art of Technical Project Management",
    publishDate: "2025-07-21",
    categories: [{ _id: "cat-1", title: "Project Management" }],
    excerpt: "Bridging the gap between development teams and stakeholders requires more than just Gantt charts. It's about empathy and clear communication.",
    author: { name: "Carla Heywood" },
  },
  {
    _id: "placeholder-2",
    slug: { current: "why-your-next-project-should-be-headless" },
    title: "Why Your Next Project Should Be Headless",
    publishDate: "2025-06-15",
    categories: [{ _id: "cat-2", title: "Web Development" }],
    excerpt: "Decoupling the frontend from the backend is not just a trend; it's a strategic move towards future-proofing your digital presence.",
    author: { name: "Carla Heywood" },
  },
  {
    _id: "placeholder-3",
    slug: { current: "fostering-collaboration-in-remote-teams" },
    title: "Fostering Collaboration in Remote Teams",
    publishDate: "2025-05-02",
    categories: [{ _id: "cat-3", title: "Teamwork" }],
    excerpt: "Discover the tools and techniques that transform remote work from a challenge into a strategic advantage for your team.",
    author: { name: "Carla Heywood" },
  },
];

export default function BlogPosts(props: BlogPostsProps) {
  const {
    heading,
    description,
    showFilters,
    showSearch,
    postsPerPage,
    categories = [],
    excludeCategories = [],
    sortBy,
    layout,
    showExcerpt,
    showAuthor,
    showDate,
    showCategory,
    showFeaturedImage,
    styling = {},
    posts = []
  } = props;

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Use placeholder posts if no posts are provided
  const allPosts = posts.length > 0 ? posts : placeholderPosts;

  // Get all unique categories from posts
  const allCategories = useMemo(() => {
    const categorySet = new Set<string>();
    allPosts.forEach(post => {
      post.categories?.forEach(cat => {
        categorySet.add(cat.title);
      });
    });
    return ["All", ...Array.from(categorySet)];
  }, [allPosts]);

  const filteredPosts = useMemo(() => {
    let filtered = allPosts;

    // Filter by category
    if (selectedCategory !== "All") {
      filtered = filtered.filter(post => 
        post.categories?.some(cat => cat.title === selectedCategory)
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (post.excerpt && post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply posts per page limit
    if (postsPerPage && postsPerPage > 0) {
      filtered = filtered.slice(0, postsPerPage);
    }

    return filtered;
  }, [allPosts, selectedCategory, searchTerm, postsPerPage]);

  return (
    <div className="container mx-auto px-6 py-16 md:py-24">
        {/* Heading and Description */}
        {(heading || description) && (
        <div className="mb-12 text-center">
            {heading && (
            <h2 className="text-3xl md:text-4xl font-bold text-[#53443C] mb-4">
                {heading}
            </h2>
            )}
            {description && (
            <p className="text-lg text-[#816C61] max-w-2xl mx-auto">
                {description}
            </p>
            )}
        </div>
        )}

        {/* Filters and Search */}
        {(showFilters || showSearch) && (
        <div className="mb-12 flex flex-col-reverse md:flex-row gap-6 items-center justify-between">
        {showFilters && (
        <div className="flex items-center gap-2 flex-wrap">
            {allCategories.map((category) => (
            <Button
                key={category}
                variant={selectedCategory === category ? "default" : "ghost"}
                onClick={() => setSelectedCategory(category)}
                className={`rounded-full min-w-25 px-4 py-2 text-sm ${
                selectedCategory === category
                    ? "bg-[#53443C] text-white hover:bg-[#53443C]/90"
                    : "text-[#816C61] hover:bg-[#F4E7DC] hover:text-[#53443C]"
                }`}
            >
                {category}
            </Button>
            ))}
        </div>
        )}
        {showSearch && (
        <div className="relative w-full md:w-auto md:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
            type="search"
            placeholder="Search articles..."
            className="pl-10 bg-white border-[#F4E7DC] focus:ring-[#AC8A77] focus:border-[#AC8A77] rounded-full w-full"
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
            />
        </div>
        )}
        </div>
        )}

        {/* Blog Post Grid */}
        {filteredPosts.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
            <Link
                href={`/blog/${post.slug.current}`}
                key={post._id}
                className="group"
            >
                <article className="bg-white border border-[#F4E7DC] rounded-2xl overflow-hidden h-full flex flex-col transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                {showFeaturedImage && (
                <div className="relative h-48 w-full">
                    <Image
                    src={post.featuredImage ? urlFor(post.featuredImage).width(800).height(600).url() : "/placeholder.svg"}
                    alt={post.featuredImage?.alt || `Image for ${post.title}`}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    className="object-cover"
                    />
                </div>
                )}
                <div className="p-6 flex flex-col flex-grow">
                    {showCategory && post.categories && post.categories.length > 0 && (
<Badge
                        variant="outline"
                        className="text-xs bg-transparent text-coffee border-border rounded-full px-3 py-1 font-normal w-fit mb-3"
                      >
                        {post.categories[0].title}
                    </Badge>
                    )}
                    <h2 className="text-xl font-medium text-[#53443C] mb-3 flex-grow">
                    {post.title}
                    </h2>
                    {showExcerpt && post.excerpt && (
                    <p className="text-sm text-[#816C61] leading-relaxed mb-4">
                        {post.excerpt}
                    </p>
                    )}
                    {(showAuthor || showDate) && (
                    <div className="flex items-center gap-3 mt-auto pt-4 border-t border-[#F4E7DC]">
                        {showAuthor && post.author && (
                        <>
                        <Image
                            src={post.author.image ? urlFor(post.author.image).width(40).height(40).url() : "/placeholder.svg"}
                            alt={post.author.name}
                            width={40}
                            height={40}
                            className="rounded-full"
                        />
                        <div>
                            <p className="text-sm font-medium text-[#53443C]">
                            {post.author.name}
                            </p>
                            {showDate && (
                            <p className="text-xs text-[#816C61]">
                                {new Date(post.publishDate).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </p>
                            )}
                        </div>
                        </>
                        )}
                    </div>
                    )}
                </div>
                </article>
            </Link>
            ))}
        </div>
        ) : (
        <div className="text-center py-16">
            <p className="text-xl text-[#53443C] font-medium">
            No articles found
            </p>
            <p className="text-[#816C61] mt-2">
            Try adjusting your search or filter.
            </p>
        </div>
        )}
    </div>
  );
}
